import { ArrowUpRight } from "lucide-react";
import { S7Logo } from "@/components/landing/S7Logo";
import { BookACallButton } from "@/components/landing/BookACallButton";
import { ReviewBadges } from "@/components/landing/ReviewBadges";
import { router } from "@/app/routes";
import { useCookieConsentStore } from "@/stores/cookieConsentStore";

/* -- Shared column primitives (all tokens reused from existing footer) -- */

function ColumnLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[14px] font-medium text-foreground m-0">{children}</p>
  );
}

function FooterLink({
  children,
  onClick,
  href,
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
}) {
  return (
    <a
      href={href ?? "#"}
      onClick={onClick}
      className="text-[14px] no-underline transition-opacity hover:opacity-70 block"
      style={{ color: "hsl(var(--foreground) / 0.40)" }}
    >
      {children}
    </a>
  );
}

/* -- Footer -- */

export function Footer() {
  const openCookiePreferences = useCookieConsentStore((s) => s.openPreferences);

  function handleNavClick(path: string) {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      // Paths served by external SSR services (news-renderer for /news/*)
      // or static doc pages live outside React — use full page navigation
      // so the routing layer proxies them correctly. Without this, /news
      // falls into the SPA's client-side route, which serves bundled
      // samplePulses (Apr 15 cutoff) instead of fresh DB-backed content.
      if (
        path.startsWith("/docs") ||
        path === "/news" ||
        path.startsWith("/news/")
      ) {
        window.location.href = path;
      } else {
        router.navigate(path);
      }
    };
  }

  function handleCookiePrefs(e: React.MouseEvent) {
    e.preventDefault();
    openCookiePreferences();
  }

  return (
    <footer
      className="px-5 py-16"
      style={{ borderTop: "1px solid hsl(var(--foreground) / 0.06)" }}
    >
      <div className="mx-auto" style={{ maxWidth: 1224 }}>
        {/* Top section -- four columns */}
        <div className="grid gap-10 lg:gap-12 grid-cols-1 md:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 text-foreground">
              <S7Logo size={20} />
              <span className="text-[16px] font-semibold">Altair</span>
            </div>
            <p
              className="text-[14px] mt-3"
              style={{
                color: "hsl(var(--foreground) / 0.40)",
                lineHeight: 1.55,
                whiteSpace: "nowrap",
              }}
            >
              Revenue cycle, built to win against payers.
              <br />
              Run by AI, backed by experts.
            </p>
            {/* Trust line: mono, HIPAA badge + Trust Center link (design L400). */}
            <div
              className="mt-4 flex items-center gap-2 font-mono"
              style={{
                fontSize: 11,
                color: "hsl(var(--foreground) / 0.5)",
              }}
            >
              HIPAA compliant
              <span style={{ opacity: 0.4 }}>·</span>
              <a
                href="https://trust.altair-health.com"
                className="no-underline"
                style={{ color: "hsl(220 60% 42%)" }}
              >
                Trust Center ↗
              </a>
            </div>
            <a
              href="https://www.s7-lab.health"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 no-underline mt-5 transition-opacity hover:opacity-70"
              style={{ color: "hsl(var(--foreground) / 0.40)" }}
            >
              <span className="text-[13px]">Built by</span>
              <span className="text-[13px] font-medium text-foreground">
                S7 Lab
              </span>
              <ArrowUpRight
                className="w-3 h-3"
                style={{ color: "hsl(var(--foreground) / 0.30)" }}
              />
            </a>
            {/* Investor credit, same shape as the S7 Lab line: label at 0.40
                ink, name at full ink. Rendered as text until Altair's YC
                directory page is confirmed live; then it becomes an anchor
                with the external arrow, like the S7 Lab credit. The YC mark
                is the same self-hosted file the hero strip uses. A block-level
                flex row, so it sits on its own line under the inline S7 Lab
                anchor. */}
            <div
              className="mt-2 flex items-center gap-1.5"
              style={{ color: "hsl(var(--foreground) / 0.40)" }}
            >
              <img
                src="/investors/yc.svg"
                alt=""
                width={12}
                height={12}
                className="block rounded-[2px]"
              />
              <span className="text-[13px]">Backed by</span>
              <span className="text-[13px] font-medium text-foreground">
                Y Combinator
              </span>
            </div>
            {/* Grouped with the trust line and the two credits above, since
                all four are third-party corroboration rather than navigation. */}
            <ReviewBadges />
          </div>

          {/* Platform */}
          <div className="flex flex-col gap-3">
            <ColumnLabel>Platform</ColumnLabel>
            <div className="flex flex-col gap-2">
              <FooterLink href="/ehr" onClick={handleNavClick("/ehr")}>
                Free EHR
              </FooterLink>
              <FooterLink
                href="/docs/solutions"
                onClick={handleNavClick("/docs/solutions")}
              >
                Solutions
              </FooterLink>
              <FooterLink
                href="/docs/new-practice"
                onClick={handleNavClick("/docs/new-practice")}
              >
                New practice billing
              </FooterLink>
              <FooterLink
                href="/docs/switch"
                onClick={handleNavClick("/docs/switch")}
              >
                Switch billing service
              </FooterLink>
              <FooterLink
                href="/docs/specialty-billing"
                onClick={handleNavClick("/docs/specialty-billing")}
              >
                Specialty billing
              </FooterLink>
              <FooterLink
                href="/docs/compare"
                onClick={handleNavClick("/docs/compare")}
              >
                Compare Altair
              </FooterLink>
              <FooterLink
                href="/docs/alternatives"
                onClick={handleNavClick("/docs/alternatives")}
              >
                Alternatives
              </FooterLink>
              <FooterLink
                href="/docs/revenue-cycle-management"
                onClick={handleNavClick("/docs/revenue-cycle-management")}
              >
                RCM guide
              </FooterLink>
              <FooterLink
                href="/docs/best-ai-rcm-software"
                onClick={handleNavClick("/docs/best-ai-rcm-software")}
              >
                Best AI RCM software
              </FooterLink>
            </div>
          </div>

          {/* Learn */}
          <div className="flex flex-col gap-3">
            <ColumnLabel>Learn</ColumnLabel>
            <div className="flex flex-col gap-2">
              <FooterLink href="/news" onClick={handleNavClick("/news")}>
                Billing News
              </FooterLink>
              <FooterLink
                href="/docs/blog"
                onClick={handleNavClick("/docs/blog")}
              >
                Blog
              </FooterLink>
              <FooterLink href="/docs" onClick={handleNavClick("/docs")}>
                Documentation
              </FooterLink>
              <FooterLink
                href="/docs/denials"
                onClick={handleNavClick("/docs/denials")}
              >
                Denial Codes
              </FooterLink>
              <FooterLink
                href="/docs/glossary"
                onClick={handleNavClick("/docs/glossary")}
              >
                Glossary
              </FooterLink>
              <FooterLink
                href="/docs/modifiers"
                onClick={handleNavClick("/docs/modifiers")}
              >
                Billing Modifiers
              </FooterLink>
              <FooterLink
                href="/docs/payers"
                onClick={handleNavClick("/docs/payers")}
              >
                Payer Guides
              </FooterLink>
              <FooterLink
                href="/docs/regulations"
                onClick={handleNavClick("/docs/regulations")}
              >
                Regulations
              </FooterLink>
              <FooterLink
                href="/docs/specialties"
                onClick={handleNavClick("/docs/specialties")}
              >
                Clinical Specialties
              </FooterLink>
              <FooterLink
                href="/docs/states"
                onClick={handleNavClick("/docs/states")}
              >
                State Laws
              </FooterLink>
            </div>
          </div>

          {/* Company */}
          <div className="flex flex-col gap-3">
            <ColumnLabel>Company</ColumnLabel>
            <div className="flex flex-col gap-2">
              <a
                href="mailto:hello@altair-health.com"
                className="text-[14px] no-underline transition-opacity hover:opacity-70 block"
                style={{ color: "hsl(var(--foreground) / 0.40)" }}
              >
                hello@altair-health.com
              </a>
              <BookACallButton
                variant="text"
                placement="footer"
                label="Talk to us"
              />
              <FooterLink href="/about" onClick={handleNavClick("/about")}>
                About us
              </FooterLink>
            </div>
          </div>

          {/* Legal */}
          <div className="flex flex-col gap-3">
            <ColumnLabel>Legal</ColumnLabel>
            <div className="flex flex-col gap-2">
              <FooterLink href="/terms" onClick={handleNavClick("/terms")}>
                Terms of service
              </FooterLink>
              <FooterLink href="/privacy" onClick={handleNavClick("/privacy")}>
                Privacy policy
              </FooterLink>
              <FooterLink href="/hipaa" onClick={handleNavClick("/hipaa")}>
                HIPAA compliance
              </FooterLink>
              <FooterLink onClick={handleCookiePrefs}>
                Cookie preferences
              </FooterLink>
              <FooterLink onClick={handleCookiePrefs}>
                Do Not Sell or Share My Personal Information
              </FooterLink>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          className="mt-12"
          style={{ borderTop: "1px solid hsl(var(--foreground) / 0.08)" }}
        />

        {/* Bottom row -- copyright */}
        <div className="pt-6">
          <p
            className="text-[13px]"
            style={{ color: "hsl(var(--foreground) / 0.25)" }}
          >
            &copy; 2026 S7 Lab, Inc. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
